import { store, store$ } from '@redux/store';
import HousingProductType from 'constants/housingProductType';
import { googleAnalytics4Event } from 'features/googleAnalytics4';
import typeahead from "features/articleSearchBox";
import { filter, fromEvent, map, merge, distinctUntilChanged } from 'rxjs';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';

const getPopularPillElement = (event) => event.target.closest('[data-pill]');
const getStandardCardElement = (event) => event.target.closest("[data-card]");
const getLinkableArticleElement = (event) => event.target.closest("[data-link]");
const isBreadcrumbItemElement = (event) => event.target?.closest('[data-breadcrumb-item]');
const seeAllArticlesButton = document.querySelector('[data-see-all-articles]');
const isListingForYouSection = (event) => !!event.target.closest("[data-listings-for-you-container]");
const isFavoriteIcon = (event) => !!event.target.closest(".icon__save");
const isCallToAction = (event) => !!event.target.closest("[data-lead-generator-opener]");
const isStandardCard = (event) => !!event.target.closest("[data-standard-card]");
const isMostPopularSection = (event) => !!event.target.closest('[data-section-name="mostPopularArticles"]');
const isCategorySection = (event) => !!event.target.closest('[data-section-name="categoryArticles"]');
const isMetroSpecificSection = (event) => !!event.target.closest('[data-section-name="metroSpecificSubjects"]');
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();

/* Observable that listens for click events on the document.body */
const bodyClick$ = fromEvent(document.body, 'click');

const listingForYouSectionClick$ = bodyClick$.pipe(
    filter((event) => isListingForYouSection(event))
  );

const breadcrumbItems = Array.from(document.querySelectorAll('[data-breadcrumb-item]'));

// *************** Breadcrumb GA Events Handlers. ***************
/* An observable stream that listens for click events. It then maps each click event to the closest
ancestor element of the clicked target that has a `data-breadcrumb-item` attribute. 
The `filter(Boolean)` function is used to filter out any falsy values. */
const onBreadcrumbClick$ = bodyClick$.pipe(
    map(isBreadcrumbItemElement),
    filter(Boolean)
  );

 /**
 * Dispatches a Google Analytics 4 event when a breadcrumb link is clicked.
 * @param breadcrumbLink - is the link element that represents a
 * breadcrumb item in a website's navigation.
 */
const triggerBreadcrumbClickEvent = (breadcrumbLink) => {
    const breadcrumbIndex = breadcrumbItems.findIndex((item) => item === breadcrumbLink);
    const customProperties = {
      breadcrumb_position: breadcrumbIndex + 1,
      destination: breadcrumbLink.href,
      link_text: breadcrumbLink.innerText,
    };
  
    store.dispatch(googleAnalytics4Event({ eventName: 'breadcrumb_click', customProperties }));
  };
  
onBreadcrumbClick$.subscribe(triggerBreadcrumbClickEvent);

/**
 * Dispatches a Google Analytics 4 event when a
 * "See All Articles" link is clicked, capturing the link text and destination URL as custom
 * properties.
 * @param element - The `element` parameter in the `triggerSeeAllArticlesLinkClickedEvent` function
 * is a reference to the HTML element that represents the "See All Articles" link.
 */
const triggerSeeAllArticlesLinkClickedEvent = (element) => {
  const customProperties = {
    button_text: 'See All Articles',
    destination_url: element.href,
  };
  store.dispatch(googleAnalytics4Event({ eventName: 'button_click', customProperties }));
};

/* Creating an observable stream `seeAllArticlesLinkClicked$` that listens for
click events on the `seeAllArticlesLink` element. When a click event is detected, the code maps
the event to the closest ancestor element with the attribute `[data-see-all-articles]`. */
const seeAllArticlesLinkClicked$ = fromEvent(seeAllArticlesButton, 'click').pipe(
  map((event) => event.target),
);

seeAllArticlesLinkClicked$.subscribe(triggerSeeAllArticlesLinkClickedEvent);

// *************** Search Box GA Events Handlers. ***************
/**
 * Dispatches a Segment event when a search is done.
 * @param suggestion - is the suggestion element that represents the element clicked
 * in the typeahead.
 */
const triggerSearchExecutedEvent = (suggestion) => {
    const customProperties = {
      search_value: suggestion.dataset.title,
      page_type: 'Learning_Center',
      click_target: 'Learn more about Pop Pills',
    };
  
    store.dispatch(googleAnalytics4Event({ eventName: "searchterm_events", customProperties })    );
  };
  
  typeahead
    .getSuggestionListClickedObserver()
    .subscribe(triggerSearchExecutedEvent);

// *************** Recommended Article Events Handlers. ***************
  /**
 * Dispatches a Segment event when an article is clicked.
 * @param article - is the article that represents the element clicked.
 */
  const triggerRecomendedArticlesClick = (article) => {
    const customProperties = {
      content_id: article.dataset.slug,
      content_title: article.dataset.title,
      recommendation_type: "similar articles",
      page_type: 'Learning_Center',
    };
  
    store.dispatch(googleAnalytics4Event({ eventName: "recommendation_click", customProperties }));
  };
  
  /* Observable stream that listens for click events on the document body.
  It then filters those events to only include ones where the clicked
  element is identified as being in the most popular section (determined by the `isMostPopularSection`
  function). */
  const onMostPopularArticleClick$ = bodyClick$.pipe(
    filter(isMostPopularSection),
    map(getLinkableArticleElement),
    filter(Boolean)
  );
  
  onMostPopularArticleClick$.subscribe(triggerRecomendedArticlesClick);
  
  /* Observable stream that listens for click events on the `document.body`.
   It then filters those events to only include ones where the clicked
   element is identified as being in the category articles section (determined by the
   `isCategorySection` function). */
  const onCategoryArticlesClick$ = bodyClick$.pipe(
    filter(isCategorySection),
    map(getLinkableArticleElement),
    filter(Boolean)
  );
  
  onCategoryArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// *************** Metro Specific Article Events Handlers. ***************
/**
 * Dispatches a Google Analytics 4 event for tracking article
 * clicks with custom properties.
 * @param article - Reference to an HTML element that represents an
 * article on a webpage. It likely has `dataset` properties such as `data-slug` and `data-title` that
 * store additional information about the article.
 */
const triggerArticlesClick = (article) => {
    const customProperties = {
      article_id: article.dataset.slug,
      article_title: article.dataset.title,
      page_type: 'Learning_Center',
    };
  
    store.dispatch(googleAnalytics4Event({ eventName: "article_click", customProperties }));
  };

/* Observable stream `onMetroSpecificArticlesClick$` that listens
  for click events on the `document.body`. Here's a breakdown of what each part of the code is
  doing: */
  const onMetroSpecificArticlesClick$ = bodyClick$.pipe(
    filter(isMetroSpecificSection),
    map(getLinkableArticleElement),
    filter(Boolean)
  );

  /* Observable stream `metroSpecificSectionReady$` that listens for a custom event
   named 'metroSpecificSectionReady' triggered on the `document.body`. */
onMetroSpecificArticlesClick$.subscribe(triggerArticlesClick);

/**
 * Checks if a price is valid and not equal to zero.
 * @param price - is a variable that represents the price of a listing
 */
const isPriced = (price) => price && price !== '0';

/**
 * getAreaClickedName: function to get the click target event based on the element clicked
 * @param {HTMLElement} element target element
 * @returns an string object
 */
const getAreaClickedName = function(element){
    const areaClickedPerCardElement = {
        image: 'Listings Image Clicked',
        communityName: 'Listing text link',
        planName: 'Listing text link',
        specAddress: 'Listing text link',
        favoriteButton: 'Listing favorite',
        default: 'Card Clicked',
    };
    
    const elementName = element.dataset.cardElement;
    return areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
}

/**
 * getModalClosePaidCommunityCustomProperties: function to get the custom properties of close button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalClosePaidCommunityCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalClosePaidPlanCustomProperties: function to get the custom properties of close button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalClosePaidPlanCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalClosePaidSpecCustomProperties: function to get the custom properties of close button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalClosePaidSpecCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',        
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
 * getRecommendedSubmittedPaidCommunityCustomProperties: function to get custom properties of Recommended button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getRecommendedSubmittedPaidCommunityCustomProperties = function(modalInformation){
  const { selectedRecommendedCommunities } = modalInformation;
  selectedRecommendedCommunities.forEach((dataset) => {
      const customProperties = {
          page_type: 'Learning_Center',
          click_target: 'Learn Center Listings Cards',
          button_name: 'Get More Info',
          event_type: 'TDV Events',
          currency_value: 'USD',
          value: 30,
          trustbuilder_reviews: dataset.HasBuilderReviews,
          min_price: dataset.PriceLow,
          max_price: dataset.PriceHigh,
          market_id: dataset.MarketId,
          market_name: `${dataset.MarketName}, ${dataset.StateAbbreviation}`,
          community_name: dataset.CommunityName,
          community_status: dataset.MarketingStatus || 'Normal',
          community_id: dataset.CommunityId,            
          price_reduction: dataset.PriceReductionLabel,
          is_limited_availability: dataset.HasLimitedInformation,
          is_priced: isPriced(dataset.PriceLow) || isPriced(dataset.PriceHigh) ? 'True' : 'False',
          is_paid: 'True',
          zip_code: dataset.Zip,
          builder_id: dataset.BuilderId,
          builder_name: dataset.BrandName
      };

      store.dispatch(googleAnalytics4Event({ eventName: 'direct_rec_lead', customProperties }));
  })
}

/**
 * getModalSubmittedPaidCommunityCustomProperties: function to get the custom properties of submitted button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalSubmittedPaidCommunityCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalSubmittedPaidPlanCustomProperties: function to get the custom properties of Submitted button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidPlanCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getModalSubmittedPaidSpecCustomProperties: function to get the custom properties of Submitted button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidSpecCustomProperties = function(data){
  const dataset = data.listingInformation;
  const destionationUrl = data.url

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
 * getLeadButtonClickedPaidCommunityCustomProperties: function to get the custom properties of lead button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getLeadButtonClickedPaidCommunityCustomProperties = function(standarCommunityCard){
  const dataset = standarCommunityCard.dataset;
  const destionationUrl = dataset?.url ?? standarCommunityCard?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.priceLow,
      max_price: dataset.priceHigh,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getLeadButtonClickedPaidPlanCustomProperties: function to get the custom properties of lead button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidPlanCustomProperties = function(standarPlanCard){
  const dataset = standarPlanCard.dataset;
  const destionationUrl = dataset?.url ?? standarPlanCard?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      plan_id: dataset.planId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'False',
      is_plan: 'True',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
* getLeadButtonClickedPaidSpecCustomProperties: function to get the custom properties of lead button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidSpecCustomProperties = function(specStandardCard){
  const dataset = specStandardCard.dataset;
  const destionationUrl = dataset?.url ?? specStandardCard?.querySelector('a')?.href;

  const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Download Brochure',
      destination: destionationUrl,
      trustbuilder_reviews: dataset.hasBuilderReviews,
      min_price: dataset.price,
      market_id: dataset.marketId,
      market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
      community_name: dataset.communityName,
      community_id: dataset.communityId,
      spec_id: dataset.specId,
      home_status: dataset.homeStatus,
      position: dataset.index,
      price_reduction: dataset.priceReductionLabel,
      is_limited_availability: dataset.hasLimitedInformation,
      is_priced: isPriced(dataset.price) ? 'True' : 'False',
      is_paid: 'True',
      is_spec: 'True',
      is_plan: 'False',
      zip_code: dataset.zip,
      builder_id: dataset.builderId,
      builder_name: dataset.brandName
  };

  return customProperties;
}

/**
 * getPaidCommunityCardProperties : function to return the respective custom properties
 * @param {dataset} dataset from the standard community card element
 * @returns custom properties object
 */
const getPaidCommunityCardProperties = function (data){
    const dataset = data.card.dataset;
    const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

    const customProperties = {
        page_type: 'Learning_Center',
        click_target: 'Learn Center Listings Cards',
        click_target1: data.clickTarget,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.priceLow,
        max_price: dataset.priceHigh,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_limited_availability: dataset.hasLimitedInformation,
        is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
        is_paid: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };

    return customProperties;
  }

/**
 * getPaidPlanCardProperties: function to return the respective custom properties
 * @param {dataset} dataset from the standard plan card element
 * @returns custom properties object
 */
const getPaidPlanCardProperties = function (data){

    const dataset = data.card.dataset;
    const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

    const customProperties = {
        page_type: 'Learning_Center',
        click_target: 'Learn Center Listings Cards',
        click_target1: data.clickTarget,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        plan_id: dataset.planId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_limited_availability: dataset.hasLimitedInformation,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'False',
        is_plan: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };

    return customProperties;
}

/**
 * getPaidSpecCardProperties: function to return the respective custom properties
 * @param {dataset} dataset from the standard plan card element
 * @returns custom properties object
 */
const getPaidSpecCardProperties = function (data){

    const dataset = data.card.dataset;
    const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;
  
    const customProperties = {
        page_type: 'Learning_Center',
        click_target: 'Learn Center Listings Cards',
        click_target1: data.clickTarget,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        spec_id: dataset.specId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_limited_availability: dataset.hasLimitedInformation,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'True',
        is_plan: 'False',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };

    return customProperties;
}

/**
 * Dispatches a Analytics Event event when a popular pill is clicked.
 * @param pill - is the pill clicked in popular pill section.
 */
const triggerPopularPillClick = function(pill) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: pill.dataset.pillText,
    destination: pill.dataset.pillUrl,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'tag_click', customProperties }));
};

/**
 * Dispatches a Segment event when listing card is clicked.
 * @param data - is the data card on the listings section.
 */
const triggerListingCardClick = (event) => {
    store.dispatch(googleAnalytics4Event({ eventName: event.name, customProperties: event.customProperties }));
};

/**
 * Dispatches a Analytics Event When the call to action is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerLeadButtonClickedEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties }));
}

/**
* Dispatches a Analytics Event When the Submitt Lead Button is clicked.
* @param customProperties - is the custom properties of the event.
*/
const triggerModalSubmittedEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'generate_lead', customProperties }));
}

/**
* Dispatches a Analytics Event When the modal is closed.
* @param customProperties - is the custom properties of the event.
*/
const triggermodalCloseEvent = function(customProperties) {
  store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

// Observers to popular pill section Events Logic
const onPopularPillClick$ = bodyClick$.pipe(
  map(getPopularPillElement),
  filter(Boolean)
);

onPopularPillClick$.subscribe(triggerPopularPillClick);

// Observers to Listing For You Section Events Logic
/* Stream of click events on standard cards within a "Listing For You"
section. It filters out events that are not standard cards, do not involve clicking on a favorite
icon, and are not call-to-action events. It then maps these filtered events to an object containing
the event name "card_clicked", the area clicked on the card, and the standard card element that was
clicked. */
const cardClickEvent$ = listingForYouSectionClick$.pipe(
    filter(event => isStandardCard(event) && !isFavoriteIcon(event) && !isCallToAction(event)),
    map(event => ({
        eventName: 'card_clicked',
        clickTarget: getAreaClickedName(event.target),
        card: getStandardCardElement(event)
    }))
);

/* Observable stream `favoriteIconClickEvent$` that listens for click
events in the `listingForYouSectionClick$` stream. It filters out events where the click target is a
favorite icon, then maps those events to an object with properties `eventName` set to 'saved',
`clickTarget` set to the name of the area clicked, and `card` set to the standard card element
associated with the event. */
const favoriteIconClickEvent$ = listingForYouSectionClick$.pipe(
    filter(event => isFavoriteIcon(event)),
    map(event => ({
        eventName: 'saved',
        clickTarget: getAreaClickedName(event.target),
        card: getStandardCardElement(event)
    }))
);

const standardCardClickEvent$ = merge(cardClickEvent$, favoriteIconClickEvent$);

/* Observable stream `standardSpecCardClick$` by piping the
`standardCardClickEvent$` observable stream. It filters the data to only include events where the
card is a special standard card using the `isSpecStandardCard` function. It then maps the data to
include custom properties obtained from `getPaidSpecCardProperties` function and the event name. */
const standardSpecCardClick$ = standardCardClickEvent$.pipe(
    filter((data) => isSpecStandardCard(data.card.dataset)),
    map((data) => ({
        customProperties: getPaidSpecCardProperties(data),
        name: data.eventName
    })),
);

/* Observable stream `standardPlanCardClick$` that listens for click
events on standard cards (`standardCardClickEvent$`). When a click event is detected on a standard
card, the code filters the data to check if the card is a standard plan card using the
`isPlanStandardCard` function. If the card is a standard plan card, the code then maps the data to
extract custom properties using the `getPaidPlanCardProperties` function and assigns the event name
to the `name` property. */
const standardPlanCardClick$ = standardCardClickEvent$.pipe(
    filter((data) => isPlanStandardCard(data.card.dataset)),
    map((data) => ({
        customProperties: getPaidPlanCardProperties(data),
        name: data.eventName
    })),
);

/* Observable stream `standardCommunityCardClick$` by filtering the
`standardCardClickEvent$` stream to only include events with community standard cards. It then maps
the data from these events to include custom properties obtained from
`getPaidCommunityCardProperties` function and the event name. */
const standardCommunityCardClick$ = standardCardClickEvent$.pipe(
    filter((data) => isCommunityStandardCard(data.card.dataset)),
    map((data) => ({
        customProperties: getPaidCommunityCardProperties(data),
        name: data.eventName
    })),
);

const standardCardClickMerged$ = merge(standardSpecCardClick$, standardPlanCardClick$, standardCommunityCardClick$);
standardCardClickMerged$.subscribe(triggerListingCardClick);

// Observers to Lead Button Clicked Event Logic
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
  filter(isCallToAction),
  map(getStandardCardElement),
  filter(Boolean),
);
const callToActionSpecClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidSpecCustomProperties)
);

const callToActionPlanClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidPlanCustomProperties)
);

const callToActionCommunityClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidCommunityCustomProperties)
);

merge(
  callToActionSpecClick$,callToActionPlanClick$,callToActionCommunityClick$
).subscribe(triggerLeadButtonClickedEvent);

// Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
  filter(selectFetchingModal),
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  map((modalInformation) => ({
      listingInformation: modalInformation.listingInformation,
      url: modalInformation.url
  })),
);

const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
  filter((data) => isSpecStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidSpecCustomProperties)
);

const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
  filter((data) => isPlanStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidPlanCustomProperties)
);

const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
  filter((data) => isCommunityStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidCommunityCustomProperties)
);

merge(
  modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
).subscribe(triggerModalSubmittedEvent);

// Observers to ModalSubmitted Event Logic
const recommendedSubmitted$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.previousName === 'recommendedScreen'),
  filter((modalInformation) => Boolean(modalInformation.selectedRecommendedCommunities)),
  distinctUntilChanged()
);

recommendedSubmitted$.subscribe(getRecommendedSubmittedPaidCommunityCustomProperties);

const modalCloseButtonClicked$ = fromEvent(document.body, "modalClosed").pipe(
  map((event) => event?.detail?.modalInformation),
  filter(Boolean)
);

const modalCloseSpecClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isSpecStandardCard(data.listingInformation)),
  map(getModalClosePaidSpecCustomProperties)
);

const modalClosePlanClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isPlanStandardCard(data.listingInformation)),
  map(getModalClosePaidPlanCustomProperties)
);

const modalCloseCommunityClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isCommunityStandardCard(data.listingInformation)),
  map(getModalClosePaidCommunityCustomProperties)
);

merge(
  modalCloseSpecClick$,modalClosePlanClick$,modalCloseCommunityClick$
).subscribe(triggermodalCloseEvent);